import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AIChatMessage,
  AIToolCallRecord,
  AIUsage,
} from "../types/ai.types";

// ---------------------------------------------------------------------------
// AI chat slice — owns the panel's open/closed state, current model, the
// running message thread, and per-turn streaming state.
//
// Not persisted in redux-persist (chat history is conversational; we keep it
// in memory only for now — add localStorage persistence later if users ask).
// ---------------------------------------------------------------------------

export type AssistantLanguage = "ml" | "en";

/**
 * What the voice overlay should open AS. `assistant` = talk to Xale AI about
 * the CRM (default). `telecaller` = Xale AI phones a LEAD as the consultancy's
 * telecaller — launched from a lead's header; the overlay shows a setup sheet
 * (objective, language, voice, agent name) before the line opens.
 */
export type VoiceCallRequest =
  | { mode: "assistant" }
  /** Without a leadId the overlay shows the lead picker first. */
  | { mode: "telecaller"; leadId?: number; leadName?: string; objective?: string };

const LANGUAGE_KEY = "xale_ai_language";
function readLanguage(): AssistantLanguage {
  try {
    return localStorage.getItem(LANGUAGE_KEY) === "en" ? "en" : "ml";
  } catch {
    return "ml";
  }
}

export interface AIChatState {
  /** A prompt queued from OUTSIDE the panel (e.g. the automation builder's
   *  "Build with AI" bar). The composer sends it through its own pipeline. */
  pendingPrompt: string | null;
  isOpen: boolean;
  /** Reply language for typed chat AND the voice call. Malayalam by default. */
  language: AssistantLanguage;
  /** The full-screen live voice call overlay. */
  voiceCallOpen: boolean;
  /** What the overlay was opened for (assistant by default). */
  voiceCallRequest: VoiceCallRequest;
  currentModelId: number | null;
  messages: AIChatMessage[];
  isStreaming: boolean;
  lastError: string | null;
  lastUsage: AIUsage | null;
}

const initialState: AIChatState = {
  pendingPrompt: null,
  isOpen: false,
  language: readLanguage(),
  voiceCallOpen: false,
  voiceCallRequest: { mode: "assistant" },
  currentModelId: null,
  messages: [],
  isStreaming: false,
  lastError: null,
  lastUsage: null,
};

const slice = createSlice({
  name: "aiChat",
  initialState,
  reducers: {
    askAI(state, action: PayloadAction<string>) {
      state.isOpen = true;
      state.pendingPrompt = action.payload;
    },
    consumePendingPrompt(state) {
      state.pendingPrompt = null;
    },
    openPanel(state) {
      state.isOpen = true;
    },
    closePanel(state) {
      state.isOpen = false;
    },
    togglePanel(state) {
      state.isOpen = !state.isOpen;
    },
    setCurrentModelId(state, action: PayloadAction<number | null>) {
      state.currentModelId = action.payload;
    },
    setLanguage(state, action: PayloadAction<AssistantLanguage>) {
      state.language = action.payload;
      try {
        localStorage.setItem(LANGUAGE_KEY, action.payload);
      } catch {
        /* private mode — ignore */
      }
    },
    openVoiceCall(state, action: PayloadAction<VoiceCallRequest | undefined>) {
      state.voiceCallRequest = action.payload ?? { mode: "assistant" };
      state.voiceCallOpen = true;
      // The overlay is mounted inside the panel tree; a telecaller call from a
      // lead header must not pop the chat drawer behind it.
      if (state.voiceCallRequest.mode === "assistant") state.isOpen = true;
    },
    closeVoiceCall(state) {
      state.voiceCallOpen = false;
      state.voiceCallRequest = { mode: "assistant" };
    },
    /** Switch what the OPEN overlay is doing (assistant ⇄ telecaller, or pick a lead). */
    setVoiceCallRequest(state, action: PayloadAction<VoiceCallRequest>) {
      state.voiceCallRequest = action.payload;
    },
    appendMessage(state, action: PayloadAction<AIChatMessage>) {
      state.messages.push(action.payload);
    },
    /**
     * Append text to the last assistant message (live streaming). If the
     * last message isn't an assistant message, this is a no-op.
     */
    appendAssistantDelta(state, action: PayloadAction<string>) {
      const last = state.messages[state.messages.length - 1];
      if (last?.role === "assistant") {
        last.content += action.payload;
      }
    },
    addToolCallToLastAssistant(state, action: PayloadAction<AIToolCallRecord>) {
      const last = state.messages[state.messages.length - 1];
      if (last?.role === "assistant") {
        last.toolCalls = [...(last.toolCalls ?? []), action.payload];
      }
    },
    updateToolCallResult(
      state,
      action: PayloadAction<{ id: string; result?: any; error?: string }>
    ) {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        const m = state.messages[i];
        if (m.role !== "assistant" || !m.toolCalls) continue;
        const tc = m.toolCalls.find((t) => t.id === action.payload.id);
        if (tc) {
          if ("result" in action.payload) tc.result = action.payload.result;
          if (action.payload.error) tc.error = action.payload.error;
          break;
        }
      }
    },
    setStreaming(state, action: PayloadAction<boolean>) {
      state.isStreaming = action.payload;
      if (!action.payload) {
        const last = state.messages[state.messages.length - 1];
        if (last?.role === "assistant") last.isStreaming = false;
      }
    },
    finalizeLastAssistant(state) {
      const last = state.messages[state.messages.length - 1];
      if (last?.role === "assistant") last.isStreaming = false;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.lastError = action.payload;
    },
    setUsage(state, action: PayloadAction<AIUsage | null>) {
      state.lastUsage = action.payload;
    },
    clearConversation(state) {
      state.messages = [];
      state.lastError = null;
      state.lastUsage = null;
      state.isStreaming = false;
    },
  },
});

export const {
  askAI,
  consumePendingPrompt,
  openPanel,
  closePanel,
  togglePanel,
  setCurrentModelId,
  setLanguage,
  openVoiceCall,
  closeVoiceCall,
  setVoiceCallRequest,
  appendMessage,
  appendAssistantDelta,
  addToolCallToLastAssistant,
  updateToolCallResult,
  setStreaming,
  finalizeLastAssistant,
  setError,
  setUsage,
  clearConversation,
} = slice.actions;

export default slice.reducer;
